import { ChangeDetectionStrategy, Component, computed, inject, output } from '@angular/core';
import { FloatingPanelService } from '@shared/floating-panel/floating-panel.service';
import { HD_BODYGRAPH_COLORS } from '../../bodygraph/hd-bodygraph-metadata';
import { HdBodygraphPlanetaryActivation } from '../../bodygraph/hd-bodygraph.models';
import { HD_LANG_RECOMPUTE_ON_LOAD, HdContentLanguageService } from '../../bodygraph/hd-content-language.service';
import { HdGateInfoService } from '../../bodygraph/hd-gate-info.service';
import { getHdPlanetDisplayConfigByPlanetId } from '../../bodygraph/planet-grid/hd-planet-display-config';
import {
  HD_JUST_NOW_TRANSIT_BASE_COLOR,
  HD_JUST_NOW_TRANSIT_COLOR,
} from '../just-now-transit/hd-just-now-transit';
import { buildHdLocksKeysOverview } from './hd-locks-keys';
import { HdLocksKeysPeriodPairToggleComponent } from './hd-locks-keys-period-pair-toggle.component';
import { openHdLocksKeysPostShiftPanel } from './hd-locks-keys-reading-panel.component';
import { HdLocksKeysOverlayToggleComponent } from './hd-locks-keys-overlay-toggle.component';
import { HdLocksKeysChartSearchComponent } from './hd-locks-keys-chart-search.component';
import { HdLocksKeysStateService } from './hd-locks-keys-state.service';
import {
  HD_LOCKS_KEYS_INTRO_PANEL_KEY,
  HdLocksKeysIntroPanelComponent,
} from './hd-locks-keys-intro-panel.component';
import {
  HdLocksKeysLockDetailComponent,
  HdLocksKeysLockDetailData,
  hdLocksKeysLockDetailPanelKey,
} from './hd-locks-keys-lock-detail.component';

/** Astronomicon-planeetchip op een lock- of key-poort (kleur = zijde of persoon). */
interface ChartSummaryPlanetChipVm {
  readonly key: string;
  readonly glyph: string;
  readonly color: string;
}

/** Eén lock die de chart raakt: via de lock-poort zelf, de key van de periode, of beide. */
interface ChartSummaryRowVm {
  readonly lockGate: number;
  readonly lockActivated: boolean;
  readonly lockPlanets: readonly ChartSummaryPlanetChipVm[];
  readonly role: string;
  readonly roleQualifier: string;
  readonly theme: string;
  readonly keyGate: number;
  readonly keyActivated: boolean;
  readonly keyPlanets: readonly ChartSummaryPlanetChipVm[];
  readonly lockTooltip: string | null;
  readonly keyTooltip: string;
}


/** Zelfde slot-/sleutel-/beide-classificatie als de badge in het L&K-paneel. */
interface ChartSummaryBadgeVm {
  readonly kind: 'both' | 'key' | 'lock' | 'open';
  readonly label: string;
  readonly counts: string;
  readonly tooltip: string;
  readonly together: boolean;
}

/**
 * Locks & Keys-basisinfo in het Geavanceerd-paneel (spec 2026-10-10): een
 * gefilterde weergave van de tabel uit de modus — enkel de locks die de chart
 * raakt (lock-poort en/of de key van de periode) — plus de slot-/sleutelbadge,
 * de drie-standen-toggle voor de chart-overlay (gedeeld met het dedicated
 * paneel) en een knop naar de volledige modus. Leest dezelfde bron als het dedicated paneel
 * ({@link HdLocksKeysStateService}, dus op composite-/cycle-tabs "samen"),
 * maar altijd in chart-scope: de scope-keuze van de modus speelt hier niet.
 */
@Component({
  selector: 'hd-locks-keys-chart-summary',
  imports: [HdLocksKeysOverlayToggleComponent, HdLocksKeysPeriodPairToggleComponent, HdLocksKeysChartSearchComponent],
  templateUrl: './hd-locks-keys-chart-summary.component.html',
  styleUrl: './hd-locks-keys-chart-summary.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HdLocksKeysChartSummaryComponent {
  /** "Toon de volledige modus"-knop: de host (chart-pagina) activeert de modus. */
  readonly modeRequested = output<void>();

  protected readonly state = inject(HdLocksKeysStateService);
  private readonly gateInfo = inject(HdGateInfoService);
  private readonly contentLang = inject(HdContentLanguageService);
  private readonly floatingPanels = inject(FloatingPanelService);

  private readonly lang = computed(() => this.contentLang.effectiveLang(), HD_LANG_RECOMPUTE_ON_LOAD);

  protected t(key: string, params?: Record<string, unknown>): string {
    return this.gateInfo.locksKeysTextIn(key, this.lang(), params);
  }

  private readonly overview = computed(() =>
    buildHdLocksKeysOverview(this.state.periodId(), 'chart', this.state.combinedActivations()),
  );

  /**
   * De getoonde chart (of op een composite een van beide) is geboren vanaf de
   * globale shift: dan vormt 19/49 geen kanaal meer (hd-global-shift.ts). De
   * pagina spiegelt gemarkeerde activaties, dus de markering zelf volstaat.
   */
  protected readonly bornAfterShift = computed(() =>
    this.state.combinedActivations().some(activation => activation.postShiftBirth === true),
  );

  protected readonly rows = computed<readonly ChartSummaryRowVm[]>(() => {
    const planetary = this.state.chartPlanetaryActivations();
    const partnerPlanetary = this.state.partnerPlanetaryActivations();
    const hasPartner = this.state.hasPartner();
    const chipsFor = (
      source: readonly HdBodygraphPlanetaryActivation[],
      prefix: string,
      gate: number,
      colorFor: (activation: HdBodygraphPlanetaryActivation) => string,
    ): ChartSummaryPlanetChipVm[] =>
      source
        .filter(activation => activation.gate === gate)
        .map(activation => ({
          key: `${prefix}-${activation.id}`,
          glyph: getHdPlanetDisplayConfigByPlanetId(activation.planetId)?.astronomiconChar ?? '',
          color: colorFor(activation),
        }));
    const sideColor = (activation: HdBodygraphPlanetaryActivation): string =>
      activation.side === 'design' ? HD_BODYGRAPH_COLORS.design : HD_BODYGRAPH_COLORS.personality;
    // Zelfde kleurconventie als het L&K-paneel: per persoon op een tab met een
    // tweede kant (chart 1 blauw, tweede kant mint), anders per zijde.
    const chips = (gate: number, prefix: string): ChartSummaryPlanetChipVm[] => hasPartner
      ? [
          ...chipsFor(planetary, `base-${prefix}`, gate, () => HD_JUST_NOW_TRANSIT_BASE_COLOR),
          ...chipsFor(partnerPlanetary, `partner-${prefix}`, gate, () => HD_JUST_NOW_TRANSIT_COLOR),
        ]
      : chipsFor(planetary, `base-${prefix}`, gate, sideColor);

    return this.overview().entries
      .filter(entry => entry.lockActivations.length > 0 || entry.keyActivated)
      .map(entry => {
        const lockGate = entry.lock.gate;
        const lockActivated = entry.lockActivations.length > 0;
        return {
          lockGate,
          lockActivated,
          lockPlanets: chips(lockGate, 'lock'),
          role: this.t(`locks.${lockGate}.role`),
          roleQualifier: this.t(`locks.${lockGate}.roleQualifier`),
          theme: this.t(`locks.${lockGate}.theme`),
          keyGate: entry.keyGate,
          keyActivated: entry.keyActivated,
          keyPlanets: chips(entry.keyGate, 'key'),
          lockTooltip: lockActivated ? null : this.t('summary.lockNotActivatedTooltip', { gate: lockGate }),
          keyTooltip: this.t(
            entry.keyActivated ? 'panel.keyActivatedTooltip' : 'panel.keyNotActivatedTooltip',
            { gate: entry.keyGate },
          ),
        };
      });
  });

  protected readonly badge = computed<ChartSummaryBadgeVm>(() => {
    const overview = this.overview();
    const lockCount = overview.entries.filter(entry => entry.lockActivations.length > 0).length;
    const keyCount = overview.activatedKeyCount;
    const kind: ChartSummaryBadgeVm['kind'] =
      keyCount > 0 && lockCount > 0 ? 'both'
        : keyCount > 0 ? 'key'
          : lockCount > 0 ? 'lock'
            : 'open';
    const labelKey =
      kind === 'both' ? 'panel.personBadgeBoth'
        : kind === 'key' ? 'panel.personBadgeKey'
          : kind === 'lock' ? 'panel.personBadgeLock'
            : 'panel.personBadgeOpen';
    return {
      kind,
      label: this.t(labelKey),
      counts: this.t('panel.personBadgeCounts', { locks: lockCount, keys: keyCount }),
      tooltip: this.t('panel.personBadgeTooltip'),
      together: this.state.hasPartner(),
    };
  });

  /** Opent het leespaneel "Mens na 2027" (uitleg bij de kanaalbreuk en de Solar Plexus). */
  protected openPostShift(): void {
    openHdLocksKeysPostShiftPanel(this.floatingPanels, this.gateInfo, this.lang());
  }

  protected requestMode(): void {
    this.modeRequested.emit();
  }

  protected openIntro(): void {
    this.floatingPanels.openOrFocus<HdLocksKeysIntroPanelComponent, undefined>({
      key: HD_LOCKS_KEYS_INTRO_PANEL_KEY,
      component: HdLocksKeysIntroPanelComponent,
      data: undefined,
      dockTab: { label: this.t('title') },
      dockZone: 'full',
      closable: true,
      openInitiallyDocked: true,
      redockOnReopen: true,
    });
  }

  protected openLockDetail(lockGate: number): void {
    this.floatingPanels.openOrFocus<HdLocksKeysLockDetailComponent, HdLocksKeysLockDetailData>({
      key: hdLocksKeysLockDetailPanelKey(lockGate),
      component: HdLocksKeysLockDetailComponent,
      data: { gate: lockGate },
      dockTab: { label: `Lock ${lockGate}` },
      dockZone: 'full',
      closable: true,
      openInitiallyDocked: true,
      redockOnReopen: true,
    });
  }
}
